import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { CallProvider } from "./contexts/CallContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import MainLayout from "./components/layout/MainLayout";
import InstallPWA from "./components/InstallPWA";
import ErrorBoundary from "./components/layout/ErrorBoundary";

// Helper to retry lazy-loaded chunks on failure (e.g. after a redeploy with new chunk hashes)
const lazyWithRetry = (componentImport) =>
  React.lazy(() =>
    componentImport().catch((error) => {
      console.error("Chunk loading failed, reloading page:", error);
      window.location.reload();
      return { default: () => null };
    })
  );

// Lazy-loaded pages for performance optimization
const LoginPage = lazyWithRetry(() => import("./pages/AuthPage"));
const ForgotPasswordPage = lazyWithRetry(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazyWithRetry(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazyWithRetry(() => import("./pages/VerifyEmailPage"));
const WalletPage = lazyWithRetry(() => import("./pages/WalletPage"));
const PrivacyPolicyPage = lazyWithRetry(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const DataPolicyPage = lazyWithRetry(() => import("./pages/DataPolicyPage"));
const HelpCenterPage = lazyWithRetry(() => import("./pages/HelpCenterPage"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const FeedPage = lazyWithRetry(() => import("./pages/FeedPage"));
const ProfilePage = lazyWithRetry(() => import("./pages/ProfilePage"));
const PostDetailPage = lazyWithRetry(() => import("./pages/PostDetailPage"));
const MessagesPage = lazyWithRetry(() => import("./pages/MessagesPage"));
const SettingsPage = lazyWithRetry(() => import("./pages/SettingsPage"));
const SearchPage = lazyWithRetry(() => import("./pages/SearchPage"));
const AdminPage = lazyWithRetry(() => import("./pages/AdminPage"));
const ReelsPage = lazyWithRetry(() => import("./pages/ReelsPage"));
const BookmarksPage = lazyWithRetry(() => import("./pages/BookmarksPage"));
const NotificationsPage = lazyWithRetry(() => import("./pages/NotificationsPage"));
const MusicPage = lazyWithRetry(() => import("./pages/MusicPage"));
const ArtistMusicProfile = lazyWithRetry(() => import("./pages/ArtistMusicProfile"));
const VideoPage = lazyWithRetry(() => import("./pages/VideoPage"));
const StudioPage = lazyWithRetry(() => import("./pages/StudioPage"));
const AdsPage = lazyWithRetry(() => import("./pages/AdsPage"));
const PitchDeckPage = lazyWithRetry(() => import("./pages/PitchDeckPage"));
const BroadcastPage = lazyWithRetry(() => import("./pages/BroadcastPage"));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  if (user && user.emailVerified === false) {
    return <Navigate to="/verify-email" replace />;
  }
  return (
    <WebSocketProvider>
      <CallProvider>
        <MainLayout>
          <ErrorBoundary>
            <React.Suspense fallback={
              <div className="flex items-center justify-center w-full min-h-[60vh]">
                <div className="w-8 h-8 border-2 border-white/20 border-t-[#38bdf8] rounded-full animate-spin shadow-[0_0_15px_rgba(56,189,248,0.5)]" />
              </div>
            }>
              {children}
            </React.Suspense>
          </ErrorBoundary>
        </MainLayout>
        <InstallPWA />
      </CallProvider>
    </WebSocketProvider>
  );
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }
  return children;
};

function AppRoutes() {
  const location = useLocation();
  return (
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/auth/google/callback" element={<AuthCallback />} />
        <Route path="/auth/apple/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/data-policy" element={<DataPolicyPage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
        <Route path="/reels" element={<ProtectedRoute><ReelsPage /></ProtectedRoute>} />
        <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/post/:id" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
        <Route path="/vox-hq" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route path="/music" element={<ProtectedRoute><MusicPage /></ProtectedRoute>} />
        <Route path="/music/profile/:userId" element={<ProtectedRoute><ArtistMusicProfile /></ProtectedRoute>} />
        <Route path="/videos" element={<ProtectedRoute><VideoPage /></ProtectedRoute>} />
        <Route path="/studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
        <Route path="/broadcast/:username" element={<ProtectedRoute><BroadcastPage /></ProtectedRoute>} />
        <Route path="/ads" element={<ProtectedRoute><AdsPage /></ProtectedRoute>} />
        <Route path="/pitchdeck" element={<PitchDeckPage />} />
        <Route path="/presentation" element={<PitchDeckPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}

function App() {
  const manifestUrl = `${window.location.origin}/tonconnect-manifest.json`;
  return (
    <ThemeProvider>
      <TonConnectUIProvider manifestUrl={manifestUrl}>
        <AuthProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <React.Suspense fallback={
                <div className="flex items-center justify-center w-full min-h-screen bg-black text-white">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-[#38bdf8] rounded-full animate-spin shadow-[0_0_15px_rgba(56,189,248,0.5)]" />
                </div>
              }>
                <AppRoutes />
              </React.Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </AuthProvider>
      </TonConnectUIProvider>
    </ThemeProvider>
  );
}

export default App;
